import { Graph } from './Graph';
import { AStarSearch, AStarSearchOptions } from './AStarSearch';
import { vec3 } from 'gl-matrix';

export type FindPathOptions<T> = {
  maxExpansions?: number;
  maxRadius?: number;
  getPosition?: (node: T) => vec3 | number[];
};

export class PathFinder<T> {
  public graph!: Graph<T>;
  public heuristic!: (a: T, b: T) => number;

  public originPosition!: vec3 | number[];
  public targetPosition!: vec3 | number[];

  constructor() {}

  public setGraph(graph: Graph<T>): PathFinder<T> {
    this.graph = graph;
    return this;
  }

  public setHeuristic(heuristic: (node: T, goal: T) => number): PathFinder<T> {
    this.heuristic = heuristic;
    return this;
  }

  public findPath(
    start: T,
    goal: T,
    originPosition: vec3 | number[],
    targetPosition: vec3 | number[],
    options: FindPathOptions<T> = {},
  ): ResultPath<T> {
    this.originPosition = originPosition;
    this.targetPosition = targetPosition;

    let pathFinder = new AStarSearch(this.graph, this.heuristic);

    const searchOptions: AStarSearchOptions<T> = {
      maxExpansions: options.maxExpansions,
      maxRadius: options.maxRadius,
      originPosition,
      getPosition: options.getPosition,
    };

    let result = pathFinder.search(start, goal, searchOptions);

    if (result.solved) {
      let path = [goal];
      let node = goal;

      while (!this.areEqual((node = result.cameFrom.get(node)!), start)) {
        path.push(node);
      }

      return {
        solved: true,
        origin: originPosition,
        target: targetPosition,
        cameFrom: result.cameFrom,
        path: path.reverse(),
        expansions: result.expansions,
        exhausted: result.exhausted,
      };
    } else {
      return {
        solved: false,
        origin: originPosition,
        target: targetPosition,
        cameFrom: result.cameFrom,
        path: [],
        expansions: result.expansions,
        exhausted: result.exhausted,
      };
    }
  }

  protected areEqual(a: T, b: T): boolean {
    return a === b;
  }
}

export type ResultPath<T> = {
  solved: boolean;
  origin: vec3 | number[];
  target: vec3 | number[];
  cameFrom: Map<T, T>;
  path: T[];
  expansions: number;
  exhausted: boolean;
};
